import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { CloudSubscription, EntitlementBillingStatus } from '@atlassian/cssxp-typescommon';
import { FetchCloudLicenseQuery, FetchCloudLicenseQueryVariables } from '@css-xp/graphtypes/src/generated-types-new';
import { BillingStatus } from '@css-xp/graphtypes';
import { getEntitlementName } from '@atlassian/temp-helper';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  EntitlementPageType,
  AnalyticsEventSource,
  ScreenEventAnalytics,
  analytics,
  AnalyticsTag,
  createButtonClickEvent,
  AnalyticsButtonLabels,
  ScreenType,
  PAGETITLE,
} from '@atlassian/temp-analytics';
import { ErrorPanel } from '@css-xp/base/error-page';
// @ts-ignore
import CloudIcon from '@css-xp/base/assets/img/icon-cloud.svg';
import {
  AppContent,
  AppPage,
  PageHeaderV2,
  DeactivatedLozenge,
  DeletedLozenge,
  ActiveLozenge,
  getCASSIBaseURL,
  useTitle,
  useFeatureToggle,
  useRecentlyViewed,
} from '@css-xp/base';
import CloudSiteTabs from './tabs';
import { fetchCloudLicense } from './queries';
import { ENABLE_RECENTLY_VISITED_HISTORY, removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import Button from '@atlaskit/button';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { UFOEventSource } from '@atlassian/temp-observability';

interface CloudSitePageProps {
  title?: string;
}

export const CloudSitePage = ({ title }: CloudSitePageProps) => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);
  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();
  const cloudLicenseResult = useQuery<FetchCloudLicenseQuery, FetchCloudLicenseQueryVariables>(fetchCloudLicense, {
    variables: {
      params: {
        id: id,
      },
    },
  });

  const { loading, error, data } = cloudLicenseResult;
  const cloudSite = data?.cloudLicense as CloudSubscription;
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(cloudSite?.cloudSiteUrl ?? '', title);
  }
  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && cloudSite && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          cloudSiteUrl: cloudSite.cloudSiteUrl,
          cloudSiteId: cloudSite.cloudSiteId,
        },
      });
    }
  }, [data, title]);

  // to track the page view experience for cloud-site page
  GlobalPageLoadExperience.startPageLoad(UFOEventSource.CLOUD_SITE_SCREEN);
  React.useEffect(() => {
    GlobalPageLoadExperience.success({ force: true });
  }, []);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorPanel error={error} />;
  }

  if (!cloudSite) {
    return null;
  }
  const { billingStatus, sen, baseProduct, productId, businessKey, platform, cloudSiteId, cloudSiteUrl } = cloudSite;
  const renderHeaderIcon = (billingStatus: EntitlementBillingStatus) => {
    return (
      <span
        className={classNames('header-icon-container avatar', {
          deactivated: billingStatus === BillingStatus.SUSPENDED,
          deleted: billingStatus === BillingStatus.DELETED,
        })}
      >
        {/* @ts-ignore */}
        <img src={CloudIcon} className="header-icon" />
      </span>
    );
  };

  const header = (
    <div className="page-header-text">
      <span>
        {baseProduct} - {getEntitlementName(cloudSite)}
      </span>
      {billingStatus === BillingStatus.SUSPENDED && <DeactivatedLozenge />}
      {billingStatus === BillingStatus.DELETED && <DeletedLozenge />}
      {billingStatus === BillingStatus.ACTIVE && <ActiveLozenge />}
    </div>
  );

  const subheader = (
    <>
      SEN-
      {sen} | {cloudSiteId}
    </>
  );

  const renderActions = (cloudSiteID: string) => {
    return (
      <Button
        id="open-in-cassi"
        target="_blank"
        href={`${getCASSIBaseURL()}/cloud-sites/${cloudSiteID}`}
        testId="cassi-redirection-button"
        appearance="primary"
        onClick={() => {
          analytics.sendUIEvent(createButtonClickEvent(AnalyticsButtonLabels.OPEN_IN_CASSI, [AnalyticsTag.REDIRECT]));
        }}
      >
        Open in CASSI
      </Button>
    );
  };

  return (
    <AppPage id="cloud-site" className="app-page-v2">
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.ENTITLEMENT_SCREEN,
          attributes: {
            baseProduct,
            productId,
            id: businessKey,
            pageType: EntitlementPageType.CLOUD_SITE,
            platform,
            cloudSiteId,
            entityName: PAGETITLE.CLOUD_SITE,
            screenType: ScreenType.V2,
          },
        }}
      />
      <AppContent>
        <PageHeaderV2
          icon={renderHeaderIcon(billingStatus)}
          header={header}
          subheader={subheader}
          actions={renderActions(cloudSite?.cloudSiteId)}
        />
        <CloudSiteTabs parentSen={sen} cloudSiteId={cloudSite?.cloudSiteId ?? ''} cloudSiteUrl={cloudSiteUrl} />
      </AppContent>
    </AppPage>
  );
};
